import React from "react";
import { Form, Button, Alert, Modal, Dropdown } from "react-bootstrap";
import { VscSend } from "react-icons/vsc";
import { LuPlus, LuX } from "react-icons/lu";
import { CgDetailsMore } from "react-icons/cg";
import AceEditor from "react-ace";
import Tooltip from "../../../controls/common/tooltip";
import ThreadView from "../common/threadView";
import WidgetBase from "../common/widgetBase";
import Alias from "components/controls/alias";
import EnvelopeControls from "../common/envelopeControls";
import { Converter } from "../../workspace/converter";
import { canMutateSession } from "../../session/mutationGate";
import Navigation from "context/nav";
import Utils from "context/utils";
import { withPreferences } from "context/preferences";
import "./index.css";

// Custom Dropdown toggle for the composer's "+" button. Lets us keep the
// existing circular styling while delegating click/keyboard handling to
// react-bootstrap's Dropdown.
const ComposerAttachToggle = React.forwardRef(({ onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="playground-thread-composer-attach"
    onClick={(e) => { e.preventDefault(); onClick(e); }}
    aria-label="Attachment menu"
  >
    <LuPlus />
  </button>
));

const PAGE_LIMIT = 10;
const THREAD_DRAIN_MAX_PAGES = 1000;

// Exported (named) for unit tests that inspect the raw class directly; the
// default export below is the withPreferences-wrapped component used by the app.
export class ThreadNode extends WidgetBase {
  constructor(props) {
    super(props);

    this.state = {
      posting: false,
      inputValue: "",
      currentUserId: null,
      postError: null,
      // Composer metadata attached to the next send.
      composerMetadata: null,            // parsed object or null
      composerMetadataModalVisible: false,
      composerMetadataDraft: "",         // textarea contents in the modal
      composerMetadataError: null,
    };

    // The read-only view: mounted below, and the handle through which a post shows itself before
    // the server has it.
    this.view = React.createRef();
    this.nodeRef = React.createRef();
    this.textareaRef = React.createRef();
    this.pendingTempIds = new Set();
    this.authorPromises = {};
    this.boundAlias = null;
    this.boundCallback = null;
    this.boundSessionId = null;
    this.mounted = false;
  }

  /**
   * Lifecycle
   */

  componentDidMount() {
    super.componentDidMount();
    this.mounted = true;

    const api = this.getApi();
    if ( api && api.getCurrentUserId ) {
      api.getCurrentUserId().then( (id) => {
        if ( !this.mounted ) return;
        this.setState({ currentUserId: id });
      }, (_error) => {
        // Anonymous — leave currentUserId as null
      });
    }

  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);
    this.checkSyncResume();
  }
  /**
   * When sync flips OFF -> ON, actively re-tail rather than trusting the event channel.
   *
   * The catch-up dispatches `session_thread_item_posted` events, but the widget cannot rely on them
   * alone: the baseline it would fetch from is the user's OWN latest post, so items written by other
   * actors before it would be missed, and the channel itself has edges — a toggle racing an
   * outstanding POST, polling pausing after 180s. Asking the view to resync covers both: it fetches
   * strictly past what it holds, or loads the thread when it holds nothing to fetch past.
   */
  checkSyncResume() {
    const currentSync = !!(this.context && this.context.sessionSync);
    const prevSync = this._lastSyncState;
    this._lastSyncState = currentSync;

    if ( prevSync !== false || currentSync !== true ) return;
    if ( this.getEnvelopeState().status !== "ready" ) return;
    if ( this.view.current ) this.view.current.resync();
  }

  /**
   * What identifies the thread this widget is showing — the view reloads when it changes.
   *
   * The same three facts the reload gate always compared: which alias, in which session, and whether
   * the envelope is ready to be read at all. Firing a fetch against an envelope that is still
   * loading, missing or of the wrong type produces an `invalid_thread_envelope` error and a second,
   * confusing warning in the body — so "not ready" is its own key, and the view holds nothing.
   */
  sourceKey() {
    const ready = this.getEnvelopeState().status === "ready";
    return `${this.getBoundAlias() || ""}|${this.getSessionId() || ""}|${ready ? "ready" : "wait"}`;
  }

  /**
   * Where the view's bytes come from. Reads only — posting stays here, on top of it.
   */
  threadSource() {
    // READINESS FIRST, and the ids only after it. This runs during render now, where the old reload
    // gate ran only once the envelope was settled — so reading a revision off a shell that has not
    // got one yet is no longer a thing that cannot happen.
    const ready = this.getEnvelopeState().status === "ready";
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    if ( !ready || !alias || !sessionId ) {
      // Nothing to read: the body is showing a banner about why, and a fetch here would add a
      // second one contradicting it.
      return { key: this.sourceKey() };
    }
    const revisionId = this.getRevisionId();
    const api = this.getApi();
    if ( !revisionId ) return { key: this.sourceKey() };
    return {
      key: this.sourceKey(),
      listOlder: ({ cursor }) => {
        const options = { ascending: false, limit: PAGE_LIMIT };
        if ( cursor ) options.cursor = cursor;
        return api.threadItemsList(revisionId, sessionId, alias, options).then((response) => {
          const data = (response && response.data) || {};
          // Newest-first on the wire, oldest-first on screen.
          return { items: (data["items"] || []).slice().reverse(), cursor: data.cursor || null };
        });
      },
      listNewer: ({ since }) => this.drainNewer(revisionId, sessionId, alias, since),
      watch: (callback) => {
        this.boundCallback = callback;
        this.boundAlias = alias;
        if ( this.context ) this.context.addThreadEventListener(alias, callback);
      },
      unwatch: (callback) => {
        if ( this.boundAlias && this.context ) {
          this.context.removeThreadEventListener(this.boundAlias, callback);
        }
        this.boundAlias = null;
        this.boundCallback = null;
      },
      authorName: (userId) => this.resolveAuthorName(userId),
      authorProfilePath: (userId) => this.resolveProfilePath(userId)
    };
  }

  /**
   * Everything strictly newer than `since`, following the cursor across pages.
   *
   * Paged because a tail can be a burst larger than one page — a sync-resume backlog is the case
   * that made it necessary. Bounded so a cursor that never clears cannot spin forever.
   */
  async drainNewer(revisionId, sessionId, alias, since) {
    const api = this.getApi();
    const collected = [];
    const seen = new Set();
    let cursor = null;
    for ( let page = 0; page < THREAD_DRAIN_MAX_PAGES; page++ ) {
      const options = { ascending: true, createdSince: since, limit: PAGE_LIMIT };
      if ( cursor ) options.cursor = cursor;
      const response = await api.threadItemsList(revisionId, sessionId, alias, options);
      if ( !this.mounted ) return [];
      const raw = (response.data && response.data["items"]) || [];
      for ( const item of raw ) {
        if ( item && !seen.has(item.id) ) {
          seen.add(item.id);
          collected.push(item);
        }
      }
      cursor = (response.data && response.data.cursor) || null;
      if ( !cursor ) break;
    }
    return collected;
  }

  componentWillUnmount() {
    this.mounted = false;
    super.componentWillUnmount();
  }
  /**
   * Accessors
   */

  getApi() {
    return this.props.data.shellInterface.getApp().api;
  }

  getRevisionId() {
    return this.props.data.shellInterface.getRevision()["id"];
  }

  getSessionId() {
    return this.context && this.context.session ? this.context.session.id : null;
  }

  getBoundAlias() {
    return Converter.getComponentPortValueData(this.props.data.component, "thread");
  }

  // Inspect session-manager state to decide what we can render and whether
  // it's safe to call the backend. Returns:
  //   - { status: "unbound" }        — no thread port binding
  //   - { status: "no-session" }     — alias set, no session selected
  //   - { status: "loading" }        — session-objects list or value still loading
  //   - { status: "missing" }        — list loaded, no object with this alias
  //   - { status: "wrong-type" }     — object exists but value.type !== "thread"
  //   - { status: "ready", value }   — object exists and is a thread
  // This also registers a subscription on the alias via `discoveredAliases`
  // so the widget re-renders when the value becomes available.
  getEnvelopeState() {
    const alias = this.getBoundAlias();
    if ( !alias ) return { status: "unbound" };

    const manager = this.context;
    if ( !manager || !manager.session ) return { status: "no-session" };

    // Subscribe to the alias so this widget re-renders when its value
    // arrives. Cleared per-render by WidgetBase.updateSubscriptions().
    this.discoveredAliases.add(alias);

    const sessionObjects = manager.sessionObjects;
    if ( sessionObjects === null || sessionObjects === undefined ) {
      return { status: "loading", reason: "Loading session objects..." };
    }

    const exists = sessionObjects.some(o => o.alias === alias);
    if ( !exists ) {
      return { status: "missing", alias };
    }

    const value = manager.getSessionData ? manager.getSessionData(alias) : undefined;
    if ( value === undefined || value === null ) {
      return { status: "loading", reason: "Loading thread..." };
    }

    if ( value.type !== "thread" ) {
      return { status: "wrong-type", alias, type: value.type || "unknown" };
    }

    return { status: "ready", value };
  }
  /**
   * Posting
   */

  // Drives the Send button's `disabled` prop only — NOT a security boundary on its own. sendItem()
  // below re-checks canMutateSession() as the actual choke point, since the Enter-key handler, the
  // composer's <Form onSubmit>, and the failed-item retry button all bypass this method entirely.
  canSend() {
    const si = this.props.data.shellInterface;
    return !!(
      this.state.inputValue.trim()
      && !this.state.posting
      && this.state.currentUserId
      && this.getEnvelopeState().status === "ready"
      // Posting a thread item is a session-data mutation, so — beyond write access — the session must
      // also be open (Task 5). See session/mutationGate.js for the shared fail-open default applied
      // when a shellInterface doesn't expose the full seam.
      && canMutateSession(si)
    );
  }

  // Tooltip copy for the composer Send button. The composer only renders while mutations are possible
  // (see showComposer in renderContent), so there is no blocked state to explain here — a disabled
  // Send means the draft is empty or a post is already in flight.
  sendTooltipText() {
    return "Send (Shift+Enter for newline)";
  }

  sendItem(textOverride) {
    // Posting a thread item is a session-data mutation, so this is the single choke point that must
    // check the session-mutation gate — the Enter-key handler, the composer's <Form onSubmit>, and the
    // failed-item retry button all funnel through here. canSend()'s check only drives the Send button's
    // `disabled` prop; it does not, on its own, stop those other paths. Mirrors the guard placement in
    // ChatNode.sendMessage() and GraphNode.applyMutations().
    if ( !canMutateSession(this.props.data.shellInterface) ) return;

    const text = (textOverride !== undefined ? textOverride : this.state.inputValue).trim();
    if ( !text || this.state.posting ) return;

    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const metadata = this.state.composerMetadata; // null if no attachment

    // Split the composer text into multiple content items at "\n---\n"
    // separators. Each non-empty segment becomes its own text content
    // item, so the user can compose a single send that contains a few
    // logically distinct chunks. Empty segments (e.g. leading/trailing
    // separators) are dropped.
    const content = text
      .split(/\n---\n/)
      .map(segment => segment.trim())
      .filter(segment => segment.length > 0)
      .map(segment => ({ type: "text", text: segment }));
    if ( content.length === 0 ) return;

    const tempId = Utils.generateUniqueId();
    this.pendingTempIds.add(tempId);

    const optimisticItem = {
      id: tempId,
      __tempId: tempId,
      __pending: true,
      user_id: this.state.currentUserId,
      content,
      created_at: Utils.currentTimeUtcIso()
    };
    if ( metadata ) {
      optimisticItem.metadata = metadata;
    }

    if ( this.view.current ) this.view.current.appendLocal(optimisticItem);
    this.setState({
      inputValue: "",
      posting: true,
      postError: null,
      // Clear the attached metadata once it has been queued — a fresh
      // attachment is required for the next send.
      composerMetadata: null
    }, () => {
      // Reset textarea height after clearing input, and keep focus in
      // the composer so the user can start typing the next message
      // immediately. (The button's onClick path moves focus to the
      // button; this restores it.)
      const ta = this.textareaRef.current;
      if ( ta ) {
        ta.style.height = "auto";
        ta.focus();
      }
    });

    const api = this.getApi();
    // Only forward metadata if the user actually attached some — the
    // backend's schema drops the field when undefined.
    const parentId = undefined;
    api.threadItemPost(revisionId, sessionId, alias, content, parentId, metadata || undefined)
      .then( (response) => {
        if ( !this.mounted ) return;
        const serverItem = response.data && response.data["item"];
        if ( !serverItem ) {
          // No item in the response — the row stays, marked failed, so it can be retried.
          if ( this.view.current ) this.view.current.failLocal(tempId);
          this.setState({
            posting: false,
            postError: "Server did not return a thread item."
          });
          this.pendingTempIds.delete(tempId);
          return;
        }

        // The view settles it: replaced in place normally, and — because a post races the tail it
        // will arrive on — left alone if a reload already brought the server item in, or appended if
        // that reload wiped the optimistic row without picking the item up.
        if ( this.view.current ) this.view.current.settleLocal(tempId, serverItem);
        this.setState({ posting: false, postError: null });
        this.pendingTempIds.delete(tempId);
      }, (error) => {
        if ( !this.mounted ) return;
        if ( this.view.current ) this.view.current.failLocal(tempId);
        this.setState({
          posting: false,
          postError: error && error.message ? error.message : "Failed to post thread item."
        });
        // Keep tempId in pendingTempIds so the user can retry.
      });
  }

  /**
   * Composer metadata attachment
   */

  openComposerMetadataModal = () => {
    const draft = this.state.composerMetadata
      ? JSON.stringify(this.state.composerMetadata, null, 2)
      : "{\n  \n}";
    this.setState({
      composerMetadataModalVisible: true,
      composerMetadataDraft: draft,
      composerMetadataError: null
    });
  }

  closeComposerMetadataModal = () => {
    this.setState({
      composerMetadataModalVisible: false,
      composerMetadataError: null
    });
  }

  applyComposerMetadata = () => {
    const text = (this.state.composerMetadataDraft || "").trim();
    if ( text === "" ) {
      // Empty metadata is treated as "no attachment".
      this.setState({
        composerMetadata: null,
        composerMetadataModalVisible: false,
        composerMetadataError: null
      });
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      this.setState({ composerMetadataError: "Invalid JSON: " + e.message });
      return;
    }
    if ( typeof parsed !== "object" || parsed === null || Array.isArray(parsed) ) {
      this.setState({ composerMetadataError: "Metadata must be a JSON object." });
      return;
    }
    this.setState({
      composerMetadata: parsed,
      composerMetadataModalVisible: false,
      composerMetadataError: null
    });
  }

  clearComposerMetadata = () => {
    this.setState({ composerMetadata: null });
  }

  /**
   * Item expansion (long content + metadata)
   */



  retryFailedItem(item) {
    if ( !item || !item.__failed ) return;
    const text = (item.content && item.content[0] && item.content[0].text) || "";
    if ( !text ) return;
    // Drop the failed row, then re-send: a retry is a new post, and leaving the old one would show
    // the same message twice once it lands.
    if ( this.view.current ) this.view.current.removeLocal(item.__tempId);
    this.pendingTempIds.delete(item.__tempId);
    this.sendItem(text);
  }
  /**
   * Composer handlers
   */

  handleKeyDown = (e) => {
    if ( e.key === "Enter" && !e.shiftKey ) {
      e.preventDefault();
      this.sendItem();
    }
  };

  handleTextareaInput = (e) => {
    const value = e.target.value;
    this.setState({ inputValue: value });
    const el = e.target;
    el.style.height = "auto";
    const lineHeight = 20;
    const maxHeight = lineHeight * 6;
    el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
  };

  /**
   * Author name resolution (cached)
   */

  resolveAuthorName(userId) {
    if ( !userId ) return "unknown";
    const api = this.getApi();
    const cachedResponse = api.usersCacheById ? api.usersCacheById[userId] : null;
    if ( cachedResponse ) {
      const user = (cachedResponse.data && cachedResponse.data["user"]) || {};
      return user["name"] || user["alias"] || user["email"] || userId.slice(-5);
    }
    if ( !this.authorPromises[userId] ) {
      this.authorPromises[userId] = api.usersIdGet(userId).then(() => {
        if ( this.mounted ) this.forceUpdate();
      }, () => {
        delete this.authorPromises[userId];
      });
    }
    return userId.slice(-5);
  }

  /**
   * Rendering
   */

  getClassName() {
    return "playground-thread-node";
  }

  renderHeaderAlias() {
    const alias = this.getBoundAlias();
    if ( !alias ) return null;
    return <Alias value={alias} variant="chip" />;
  }

  renderHeaderActions() {
    const envelope = this.getEnvelopeState();
    const value = envelope.status === "ready" ? envelope.value : null;
    return (
      <EnvelopeControls
        alias={this.getBoundAlias()}
        value={value}
        shellInterface={this.props.data.shellInterface}
        sessionManager={this.context}
        refreshTooltip="Refresh thread"
        // Reloading the envelope value alone doesn't refresh the messages —
        // those come from threadItemsList. Re-run the initial load so a
        // refresh re-fetches the thread items too.
        onRefresh={() => this.initialLoad()}
      />
    );
  }



  resolveProfilePath(userId) {
    if ( !userId ) return null;
    // Prefer the user's alias for a cleaner URL, falling back to the id.
    const api = this.getApi();
    const cached = api && api.usersCacheById ? api.usersCacheById[userId] : null;
    const user = cached && cached.data ? cached.data.user || cached.data : null;
    const slug = (user && user.alias) || userId;
    return Navigation.userProfilePath(slug);
  }

  renderContent() {
    const { width, height, maxHeight } = this.getEffectiveSize();

    const contentView = Converter.getComponentPortValueData(this.props.data.component, "content_view");
    const envelopeState = this.getEnvelopeState();
    const isReady = envelopeState.status === "ready";

    // One rule for every mutation surface in the playground: it renders only while mutations are
    // actually possible. canMutateSession() collapses ALL the reasons they might not be — no write
    // access, a committed revision, a closed session — so the composer behaves the same way whichever
    // one applies, instead of vanishing for one and lingering disabled for another. Why mutations are
    // blocked isn't surfaced anywhere — the control is simply absent.
    const showComposer = canMutateSession(this.props.data.shellInterface) && isReady;

    return (
      <div ref={this.nodeRef} className="playground-thread-node" style={{ width, height, maxHeight }}>
        {envelopeState.status === "unbound" &&
          this.renderNotice({ variant: "info", message: 'Connect a value to the "thread" port.' })
        }
        {envelopeState.status === "no-session" &&
          this.renderNotice({ variant: "info", message: "Select a session to view this widget." })
        }
        {envelopeState.status === "loading" &&
          this.renderLoading(envelopeState.reason || "Loading…")
        }
        {envelopeState.status === "missing" &&
          this.renderNotice({
            variant: "info",
            message: `"${envelopeState.alias}" is not available in the selected session yet.`,
          })
        }
        {envelopeState.status === "wrong-type" &&
          this.renderNotice({
            variant: "warning",
            message: `The "thread" port expects a "thread" value, but "${envelopeState.alias}" is "${envelopeState.type}".`,
          })
        }
        {/* THE VIEW (common/threadView.js): loading, paging, the live tail, scroll behaviour and
            the item rendering. Mission Control's cell screen mounts the same one for a run's
            transcript, so a change to how a thread READS reaches both without being made twice.
            It is read-only, and posting sits on top of it — the composer below, and the optimistic
            row handed to it through the handles it exposes for exactly that. */}
        {isReady &&
          <ThreadView
            ref={this.view}
            source={this.threadSource()}
            contentView={contentView}
            currentUserId={this.state.currentUserId}
            timezone={this.props.preferences && this.props.preferences["app.timezone"]}
            emptyMessage="No messages yet. Type something below to start the thread."
            onRetry={(item) => this.retryFailedItem(item)}
          />
        }

        {showComposer &&
          <div className="playground-thread-composer">
            {this.state.postError &&
              <Alert variant="warning" className="playground-thread-composer-error">
                {this.state.postError}
              </Alert>
            }
            {this.state.composerMetadata &&
              <button
                type="button"
                className="playground-thread-composer-attached"
                onClick={this.openComposerMetadataModal}
              >
                <CgDetailsMore className="playground-thread-composer-attached-icon" />
                <span className="playground-thread-composer-attached-label">
                  metadata
                </span>
                <Tooltip text="Remove">
                  <span
                    role="button"
                    tabIndex={0}
                    className="playground-thread-composer-attached-remove"
                    onClick={(e) => { e.stopPropagation(); this.clearComposerMetadata(); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        this.clearComposerMetadata();
                      }
                    }}
                  >
                    <LuX />
                  </span>
                </Tooltip>
              </button>
            }
            <Form onSubmit={(event) => { event.preventDefault(); this.sendItem(); }}>
              <div className="bh-composer-shell">
                <Dropdown drop="up">
                  <Dropdown.Toggle as={ComposerAttachToggle} />
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={this.openComposerMetadataModal}>
                      <CgDetailsMore />&nbsp;&nbsp;{this.state.composerMetadata ? "Edit Metadata" : "Add Metadata"}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <Form.Control
                  as="textarea"
                  rows={1}
                  ref={this.textareaRef}
                  className="bh-composer-textarea nodrag nowheel"
                  placeholder="Type a message..."
                  value={this.state.inputValue}
                  onChange={this.handleTextareaInput}
                  onKeyDown={this.handleKeyDown}
                />
                <Tooltip text={this.sendTooltipText()}>
                  <Button
                    className="bh-composer-send"
                    disabled={!this.canSend()}
                    onClick={() => this.sendItem()}
                  >
                    <VscSend />
                  </Button>
                </Tooltip>
              </div>
            </Form>
          </div>
        }

        {this.renderMetadataModal()}
      </div>
    );
  }

  renderMetadataModal() {
    if ( !this.state.composerMetadataModalVisible ) return null;
    return (
      <Modal
        show={this.state.composerMetadataModalVisible}
        onHide={this.closeComposerMetadataModal}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Attach Metadata</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AceEditor
            name="playground-thread-composer-metadata-editor"
            width="100%"
            mode="json"
            theme="monokai"
            wrapEnabled="true"
            value={this.state.composerMetadataDraft}
            onChange={(value) => this.setState({
              composerMetadataDraft: value, composerMetadataError: null
            })}
            minLines={5}
            maxLines={24}
            setOptions={{ printMarginColumn: 120 }}
          />
          <div style={{ marginTop: "10px", color: "#6b7280", fontSize: "13px" }}>
            Metadata attached to the next sent message. Must be a JSON object.
            Leave empty to send without metadata.
          </div>
          {this.state.composerMetadataError &&
            <Alert variant="warning" style={{ marginTop: "10px", marginBottom: 0 }}>
              {this.state.composerMetadataError}
            </Alert>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.closeComposerMetadataModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={this.applyComposerMetadata}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withPreferences(
  ThreadNode,
  ["app.timezone"],
  // Resolve preferences with the same org/blob scope the playground shell
  // uses, so blob- or org-level timezone overrides are honored. Without
  // this, withPreferences falls back to the user-level scope only and any
  // blob-scoped timezone is silently ignored.
  props => props.data.shellInterface.getScope()
);
